import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { PrintLetterHead, useLetterHead } from '../../components/ui/PrintLetterHead';
import { useOpdRegistration } from './opd.queries';
import { currency, formatOpdDateTime } from './opd.utils';

const upper = (value: string | number | null | undefined) => String(value ?? '-').toUpperCase();

export const OpdBillPrintPage = () => {
  const { registrationId } = useParams();
  const query = useOpdRegistration(registrationId);
  const letterHead = useLetterHead();

  useEffect(() => {
    if (query.data && letterHead.isReady) window.setTimeout(() => window.print(), 300);
  }, [query.data, letterHead.isReady]);

  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState title="OPD registration not found" message="The registration could not be loaded." />;

  const row = query.data;
  const patient = row.patientSnapshot;
  const age = `${patient.ageYears}Y ${patient.ageMonths}M ${patient.ageDays}D`;
  const address = [patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(', ') || '-';
  return (
    <div className="print-bill-page">
      <PrintLetterHead imageUrl={letterHead.imageUrl} />

      <table className="print-bill-header">
        <tbody>
          <tr>
            <td colSpan={2} className="print-bill-title">BILL DETAILS ( OPD PATIENT )</td>
          </tr>
          <tr>
            <td>
              <p className="print-bill-patient-name">PATIENT NAME : {upper(patient.name)}</p>
              <p><b>AGE :</b> {age} &nbsp;&nbsp;&nbsp;&nbsp; <b>SEX :</b> {upper(patient.gender)}</p>
              <p><b>ADDRESS :</b> {upper(address)}</p>
              <p><b>MOBILE NO:</b> {upper(patient.phone)}</p>
            </td>
            <td>
              <p><b>BILL NO. &amp; DATE: </b>{upper(row.billing.billNumber)} <b>&amp;</b> {upper(formatOpdDateTime(row.appointmentDate))}</p>
              <p><b>OPD NO:</b> {row.opdNumber}</p>
              <p><b>CONSULTANT DOCTOR:</b> DR. {upper(row.doctor.name)}</p>
              <p><b>DEPARTMENT:</b> {upper(row.department.name)}</p>
              <p><b>REFERRED BY :</b> {upper(row.referredBy?.name)}</p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className="print-bill-charges">
        <thead>
          <tr>
            <th className="print-bill-col-index">#</th>
            <th>DATE</th>
            <th>PARTICULARS</th>
            <th className="print-bill-col-num">QTY</th>
            <th className="print-bill-col-num">RATE</th>
            <th className="print-bill-col-num">DIS.</th>
            <th className="print-bill-col-num">AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {row.charges.map((line, index) => (
            <tr key={line.charge}>
              <td>{index + 1}</td>
              <td>{upper(formatOpdDateTime(row.appointmentDate))}</td>
              <td>{upper(line.chargeName)}</td>
              <td className="print-bill-col-num">{line.quantity}</td>
              <td className="print-bill-col-num">{line.rate}</td>
              <td className="print-bill-col-num">{line.discountAmount}</td>
              <td className="print-bill-col-num">{line.amount}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={6} className="print-bill-col-num">SUB TOTAL</th>
            <th className="print-bill-col-num">{row.billing.total}</th>
          </tr>
        </tfoot>
      </table>

      <table className="print-bill-totals">
        <tbody>
          <tr>
            <td>TOTAL :</td>
            <td className="print-bill-col-num">{currency(row.billing.total)}</td>
          </tr>
          <tr>
            <td>NET AMOUNT :</td>
            <td className="print-bill-col-num">{currency(row.billing.total)}</td>
          </tr>
          <tr>
            <td>DISCOUNT :</td>
            <td className="print-bill-col-num">{currency(row.billing.totalDiscount)}</td>
          </tr>
          <tr>
            <td>TOTAL PAYABLE AMOUNT :</td>
            <td className="print-bill-col-num">{currency(row.billing.grandTotal)}</td>
          </tr>
          <tr>
            <td>TOTAL PAID AMOUNT :</td>
            <td className="print-bill-col-num">{currency(row.billing.totalPayment)}</td>
          </tr>
          {row.billing.dueAmount > 0 ? (
            <tr className="print-bill-due-row">
              <td>DUE AMOUNT :</td>
              <td className="print-bill-col-num">{currency(row.billing.dueAmount)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <div className="print-bill-signoff">
        <span>PREPARED BY: {upper(row.generatedBy.name)}</span>
        <span>DATE: {upper(formatOpdDateTime(new Date().toISOString()))}</span>
      </div>
    </div>
  );
};
